import { useState, useEffect } from "react";
import { R2_PUBLIC_URL } from "../r2";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import videoLinks from "../data/videos.json";

const GalleryItem = ({ item, isVideo, onClick }) => {
  const [loaded, setLoaded] = useState(false);

  // Random height for masonry effect placeholder
  const placeholderHeight = item.type === 'youtube' ? 'h-48' : ['h-64', 'h-80', 'h-96'][Math.floor(Math.random() * 3)];

  return (
    <div 
      className={`break-inside-avoid relative group cursor-pointer rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition duration-500 bg-black/5 ${!loaded ? placeholderHeight : ''}`}
      onClick={onClick}
    >
      {!loaded && (
        <div className="absolute inset-0 bg-gray-200/50 animate-pulse flex items-center justify-center">
          <svg className="w-10 h-10 text-gray-300" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 18">
            <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z"/>
          </svg>
        </div>
      )}
      
      {item.type === 'youtube' ? (
        <img 
          src={item.thumbnail} 
          alt={item.title}
          className={`w-full h-auto object-cover transform transition duration-700 group-hover:scale-110 opacity-80 ${loaded ? 'opacity-80' : 'opacity-0'}`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
        />
      ) : isVideo ? (
        <video 
          src={item.url} 
          className={`w-full h-auto object-cover transform transition duration-700 group-hover:scale-110 pointer-events-none ${loaded ? 'opacity-100' : 'opacity-0'}`} 
          muted loop autoPlay playsInline 
          onLoadedData={() => setLoaded(true)}
        />
      ) : (
        <img 
          src={item.url} 
          alt={item.name}
          className={`w-full h-auto object-cover transform transition duration-700 group-hover:scale-110 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
        />
      )}
      
      {loaded && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition duration-500 flex items-center justify-center">
          <span className="text-white opacity-0 group-hover:opacity-100 transition duration-500 transform scale-50 group-hover:scale-100">
            {isVideo ? (
              <svg className="w-12 h-12 drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
            ) : (
              <svg className="w-10 h-10 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" /></svg>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        let r2Images = [];
        if (R2_PUBLIC_URL) {
          const response = await fetch(`${R2_PUBLIC_URL}/metadata.json?t=${Date.now()}`);
          if (response.ok) {
            r2Images = await response.json();
            // tag them so we know they are from R2
            r2Images = r2Images.map(img => ({ ...img, source: 'r2' }));
          }
        }
        
        // Merge R2 items with local JSON video links
        const mergedGallery = [...videoLinks, ...r2Images];
        
        setImages(mergedGallery);
      } catch (error) {
        console.error("Error fetching images from R2:", error);
        setImages([...videoLinks]); // fallback to just local videos
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  return (
    <div className="min-h-screen bg-bgWarm flex flex-col">
      <Header />
      
      <main className="flex-grow pt-28 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center mb-16 animate-fade-in-up">
            <h1 className="text-4xl md:text-5xl font-bold font-Philosopher text-heads mb-4">
              Divine Gallery
            </h1>
            <div className="h-1 w-24 bg-gradient-to-r from-main to-accent mx-auto rounded-full"></div>
            <p className="mt-6 text-xl text-heads/80 font-Philosopher max-w-2xl mx-auto">
              A collection of sacred moments, beautiful memories, and divine grace.
            </p>
          </div>

          {loading ? (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className={`break-inside-avoid rounded-2xl bg-gray-200/50 animate-pulse ${['h-64', 'h-80', 'h-96'][i % 3]}`}></div>
              ))}
            </div>
          ) : images.length === 0 ? (
            <div className="text-center text-heads/60 text-lg py-20 bg-white/30 backdrop-blur-md rounded-3xl border border-white/50 shadow-xl">
              <span className="text-4xl block mb-4">🖼️</span>
              No images in the gallery yet.
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
              {images.map((item, index) => {
                const isR2Video = item.source === 'r2' && item.name.match(/\.(mp4|webm|ogg|mov)$/i);
                const isExternalVideo = item.type === 'youtube' || item.type === 'external_video';
                const isVideo = isR2Video || isExternalVideo;
                
                return (
                  <GalleryItem key={index} item={item} isVideo={isVideo} onClick={() => setLightboxImage({ ...item, isVideo })} />
                )
              })}
            </div>
          )}
        </div>
      </main>

      {/* Lightbox Overlay */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xl flex justify-center items-center p-4 md:p-10 animate-fade-in-up"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-6 right-6 text-white/70 hover:text-white transition z-10"
            onClick={() => setLightboxImage(null)}
          >
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          
          {lightboxImage.type === 'youtube' ? (
            <div className="w-full max-w-5xl aspect-video relative">
              <iframe 
                src={lightboxImage.url} 
                title="YouTube video player" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
                className="absolute inset-0 w-full h-full rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              ></iframe>
            </div>
          ) : lightboxImage.isVideo ? (
            <video 
              src={lightboxImage.url} 
              controls autoPlay 
              className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()} 
            />
          ) : (
            <img 
              src={lightboxImage.url} 
              alt="Expanded view" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}

      <Footer />
    </div>
  );
}
